/**
 * Visitor identity for the memory platform — shared by every chat surface (public widget,
 * dashboard Playground) so the backend recognises the same browser as the same person across
 * reloads, return visits and surfaces.
 */

const ANONYMOUS_ID_KEY = 'agentic_visitor_id';
const CONVERSATION_KEY_PREFIX = 'agentic_conversation_id:';
const ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2; // 2 years
const ANONYMOUS_ID_RE = /^[A-Za-z0-9_-]{8,128}$/;

function readCookie(name: string): string | undefined {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  try {
    // SameSite=None + Secure is required for the cookie to be sent at all from inside the
    // cross-origin iframe this widget is embedded in — without it, a third-party browser
    // context either never receives the cookie or the browser rejects it outright.
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=None; Secure`;
  } catch {
    /* cookies unavailable (e.g. blocked by browser policy) — caller falls back */
  }
}

/**
 * A stable, opaque id for this browser/visitor — created once and kept in a long-lived cookie so
 * a reload or a return visit is recognised as the same person by the backend's memory platform.
 *
 * Reads/writes a cookie (not localStorage) because this widget runs inside a cross-origin iframe:
 * a `SameSite=None; Secure` cookie is the mechanism built for that exact cross-site case, whereas
 * localStorage in a third-party iframe is exactly what browsers (Safari ITP today, Chrome's
 * upcoming partitioning) are most aggressive about capping or isolating.
 *
 * A prior version of this function used localStorage for the same key. Any visitor who already
 * has an id stored that way keeps that exact id — it's read once as a migration source and
 * carried over into the cookie — so switching mechanisms doesn't create a second, disconnected
 * Visitor record for returning visitors.
 *
 * Falls back to a per-load id when no storage is available at all (private mode, everything
 * blocked) — the chat still works, it just can't be remembered, same as before this change.
 */
export function getOrCreateAnonymousId(): string {
  const make = () =>
    typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function'
      ? String((crypto as any).randomUUID()).replace(/-/g, '')
      : `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

  try {
    const fromCookie = readCookie(ANONYMOUS_ID_KEY);
    if (fromCookie && ANONYMOUS_ID_RE.test(fromCookie)) {
      // Sliding expiry — a returning visitor's cookie keeps renewing instead of expiring on them.
      writeCookie(ANONYMOUS_ID_KEY, fromCookie, ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS);
      return fromCookie;
    }

    // Migration path: carry an existing localStorage-era id forward into the cookie.
    let fromLegacyStorage: string | undefined;
    try {
      const legacy = localStorage.getItem(ANONYMOUS_ID_KEY);
      if (legacy && ANONYMOUS_ID_RE.test(legacy)) fromLegacyStorage = legacy;
    } catch {
      /* localStorage unavailable — no legacy id to migrate */
    }

    const id = fromLegacyStorage || make();
    writeCookie(ANONYMOUS_ID_KEY, id, ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS);
    return id;
  } catch {
    return make();
  }
}

/**
 * The conversation this chatbot was last in on this browser, so a reload, a brand-new tab, or a
 * separate window all resume it instead of starting over.
 *
 * Deliberately `localStorage`, not `sessionStorage`: there is no way for JavaScript to detect "a
 * new tab in the same window" versus "a genuinely new window" — a new tab only inherits
 * `sessionStorage` when it's opened FROM the page itself (a link/button), which doesn't cover
 * opening a blank tab and pasting the URL in. `localStorage` is shared by every tab and window of
 * the same browser/origin, with no such gap — the tradeoff (explicitly accepted) is that a truly
 * separate window now also resumes this conversation, rather than starting a fresh one.
 */
export function readStoredConversationId(chatbotId: string | undefined): string | undefined {
  if (!chatbotId) return undefined;
  try {
    return localStorage.getItem(`${CONVERSATION_KEY_PREFIX}${chatbotId}`) || undefined;
  } catch {
    return undefined;
  }
}

export function storeConversationId(chatbotId: string | undefined, conversationId: string | undefined): void {
  if (!chatbotId) return;
  try {
    const key = `${CONVERSATION_KEY_PREFIX}${chatbotId}`;
    if (conversationId) localStorage.setItem(key, conversationId);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable — nothing to persist */
  }
}
